import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Brain, Target, TrendingUp, Calendar, CheckCircle2, Clock, BookOpen,
  Zap, Award, ChevronRight, ChevronLeft, Play, BarChart3, Lightbulb,
  Star, Trophy, Sparkles, Rocket, ArrowRight, Lock, RefreshCw,
  Calculator, BookA, Shapes, X
} from 'lucide-react';
import { toast } from 'sonner';
import { resolveTaskTarget } from '@/utils/learningPathRouter';
import FocusCelebration from '@/components/focus/FocusCelebration';

const API = process.env.REACT_APP_BACKEND_URL;

// Subject icons and colors
const SUBJECT_CONFIG = {
  maths: { icon: Calculator, color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-600', borderColor: 'border-blue-200' },
  english: { icon: BookA, color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-600', borderColor: 'border-green-200' },
  verbal_reasoning: { icon: Brain, color: 'purple', bgColor: 'bg-purple-100', textColor: 'text-purple-600', borderColor: 'border-purple-200' },
  non_verbal_reasoning: { icon: Shapes, color: 'orange', bgColor: 'bg-orange-100', textColor: 'text-orange-600', borderColor: 'border-orange-200' }
};

// Task type configuration
const TASK_TYPE_CONFIG = {
  revision: { icon: BookOpen, color: 'blue', label: 'Revision', bgColor: 'bg-blue-100', textColor: 'text-blue-600' },
  practice: { icon: Target, color: 'purple', label: 'Practice', bgColor: 'bg-purple-100', textColor: 'text-purple-600' },
  quiz: { icon: Zap, color: 'amber', label: 'Quiz', bgColor: 'bg-amber-100', textColor: 'text-amber-600' }
};

const SixteenWeekCalendar = ({ 
  weeklyPlan = [], 
  focusTopics = [], 
  onTaskComplete,
  onRefresh,
  compact = false,
  showHeader = true
}) => {
  const navigate = useNavigate();
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [selectedDay, setSelectedDay] = useState(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [completedTasks, setCompletedTasks] = useState({});
  // Running tally of completed tasks → drives the XP celebration banner.
  const [xpEarned, setXpEarned] = useState(0);
  const token = localStorage.getItem('token');
  // Cached current user — used for stage-aware task routing.
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();

  // Load completed tasks from backend
  const loadProgress = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/calendar/progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCompletedTasks(data.completed_tasks || {});
      }
    } catch (err) {
      // Silent fail — tasks just show as incomplete
    }
  }, [token]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  // Toggle task completion
  const toggleTaskComplete = async (taskId) => {
    if (!token) return;
    const isCurrentlyComplete = !!completedTasks[taskId];
    const newState = !isCurrentlyComplete;

    // Optimistic update
    setCompletedTasks(prev => {
      const updated = { ...prev };
      if (newState) {
        updated[taskId] = true;
      } else {
        delete updated[taskId];
      }
      return updated;
    });

    try {
      const res = await fetch(`${API}/api/calendar/toggle-task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ task_id: taskId, completed: newState })
      });
      if (res.ok) {
        const data = await res.json();
        if (newState) {
          // Reward XP based on task type — revision is foundational, quizzes
          // are most demanding, practice sits in between. Bump triggers the
          // FocusCelebration banner + confetti via correctCount, and the
          // global <XpLevelBadge> via the 'edpies.xpEarned' window event.
          const xpByType = { revision: 10, practice: 15, quiz: 25 };
          const reward = xpByType[selectedTask?.type || 'revision'] || 15;
          setXpEarned((x) => x + reward);
          window.dispatchEvent(new CustomEvent('edpies.xpEarned', { detail: { amount: reward } }));
          toast.success(`+${reward} XP — Task completed!`, { duration: 1800 });
        }
        if (onTaskComplete) onTaskComplete(taskId, newState, data.total_completed);
      }
    } catch (err) {
      // Revert on failure
      setCompletedTasks(prev => {
        const reverted = { ...prev };
        if (isCurrentlyComplete) {
          reverted[taskId] = true;
        } else {
          delete reverted[taskId];
        }
        return reverted;
      });
    }
  };

  // Generate 16 weeks structure
  const generateWeeksStructure = () => {
    const weeks = [];
    for (let w = 1; w <= 16; w++) {
      const weekData = weeklyPlan.find(wp => wp.week === w) || null;
      const isPopulated = w <= 4 && focusTopics.length > 0;
      const isLocked = w > 4 && !weekData;
      
      weeks.push({
        week: w,
        label: `Week ${w}`,
        status: weekData ? 'active' : (isLocked ? 'locked' : 'upcoming'),
        days: weekData?.days || generateWeekDays(w, isPopulated),
        focusTopic: isPopulated ? focusTopics[(w - 1) % focusTopics.length] : null,
        progress: weekData?.progress || 0
      });
    }
    return weeks;
  };

  // Generate days for a week
  const generateWeekDays = (weekNum, isPopulated) => {
    if (!isPopulated) return [];
    
    const focusTopic = focusTopics[(weekNum - 1) % focusTopics.length];
    if (!focusTopic) return [];
    
    const days = [];
    const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    const taskTypes = ['revision', 'practice', 'practice', 'quiz', 'revision'];
    
    dayLabels.forEach((label, idx) => {
      days.push({
        day: idx + 1,
        label,
        tasks: [{
          id: `w${weekNum}-d${idx + 1}`,
          type: taskTypes[idx],
          title: `${TASK_TYPE_CONFIG[taskTypes[idx]].label}: ${focusTopic.topic_name || focusTopic.topic}`,
          topic: focusTopic.topic,
          subject: focusTopic.subject,
          description: getTaskDescription(taskTypes[idx], focusTopic),
          estimatedTime: taskTypes[idx] === 'quiz' ? 10 : 15,
          difficulty: focusTopic.avg_score < 40 ? 'easy' : (focusTopic.avg_score < 60 ? 'medium' : 'hard'),
          completed: false
        }]
      });
    });
    
    return days;
  };

  const getTaskDescription = (type, topic) => {
    const topicName = topic.topic_name || topic.topic?.replace(/_/g, ' ');
    switch (type) {
      case 'revision':
        return `Review core concepts of ${topicName}`;
      case 'practice':
        return `Complete practice questions on ${topicName}`;
      case 'quiz':
        return `Test your knowledge with a quick quiz on ${topicName}`;
      default:
        return `Study ${topicName}`;
    }
  };

  const weeks = generateWeeksStructure();
  const currentWeek = weeks.find(w => w.week === selectedWeek);

  const handleTaskClick = (task, dayLabel) => {
    setSelectedTask({ ...task, dayLabel });
    setTaskModalOpen(true);
  };

  const handleStartTask = () => {
    if (!selectedTask) return;
    const { path } = resolveTaskTarget(selectedTask, currentUser);
    // Stash the task id so the destination surface knows which 16-week task
    // to mark complete on return (read by useEffect on the target page).
    try { sessionStorage.setItem('edpies.activeLearningTask', JSON.stringify({
      id: selectedTask.id, type: selectedTask.type, topic: selectedTask.topic, subject: selectedTask.subject,
    })); } catch { /* ignore */ }
    setTaskModalOpen(false);
    navigate(path);
  };

  const getWeekStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-purple-600 text-white';
      case 'locked': return 'bg-gray-200 text-gray-400';
      case 'upcoming': return 'bg-gray-100 text-gray-500';
      default: return 'bg-gray-100 text-gray-500';
    }
  };

  const getWeekProgress = (week) => {
    if (week.status === 'locked') return 0;
    if (!week.days.length) return 0;
    const totalTasks = week.days.reduce((acc, day) => acc + day.tasks.length, 0);
    const completedCount = week.days.reduce((acc, day) => acc + day.tasks.filter(t => !!completedTasks[t.id]).length, 0);
    return totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Celebrations: fires confetti + banner at 5/10/25 completed tasks. */}
      <FocusCelebration correctCount={Object.keys(completedTasks).length} />
      {showHeader && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
              16-Week Learning Plan
            </h2>
            <p className="text-gray-500 text-xs md:text-sm mt-1">
              Your personalized study schedule based on assessment results
            </p>
          </div>
          {onRefresh && (
            <Button variant="outline" onClick={onRefresh} size="sm" className="w-full sm:w-auto">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Plan
            </Button>
          )}
        </div>
      )}

      {/* Week Navigation */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 md:p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedWeek(Math.max(1, selectedWeek - 4))}
            disabled={selectedWeek <= 4}
            className="px-2 md:px-3"
          >
            <ChevronLeft className="w-4 h-4 md:mr-1" />
            <span className="hidden md:inline">Previous</span>
          </Button>
          <span className="text-xs md:text-sm font-medium text-gray-600">
            Weeks {Math.floor((selectedWeek - 1) / 4) * 4 + 1} - {Math.min(Math.floor((selectedWeek - 1) / 4) * 4 + 4, 16)}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedWeek(Math.min(16, selectedWeek + 4))}
            disabled={selectedWeek > 12}
            className="px-2 md:px-3"
          >
            <span className="hidden md:inline">Next</span>
            <ChevronRight className="w-4 h-4 md:ml-1" />
          </Button>
        </div>

        {/* Week Selector Grid - 2 cols on mobile, 4 on larger */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 md:mb-4">
          {weeks.slice(Math.floor((selectedWeek - 1) / 4) * 4, Math.floor((selectedWeek - 1) / 4) * 4 + 4).map((week) => (
            <button
              key={week.week}
              onClick={() => week.status !== 'locked' && setSelectedWeek(week.week)}
              className={`relative p-2 md:p-3 rounded-lg border-2 transition-all ${
                selectedWeek === week.week
                  ? 'border-purple-500 bg-purple-50'
                  : week.status === 'locked'
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                    : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50 cursor-pointer'
              }`}
              disabled={week.status === 'locked'}
              data-testid={`week-${week.week}-btn`}
            >
              <div className="text-center">
                <span className={`text-xs md:text-sm font-medium ${
                  selectedWeek === week.week ? 'text-purple-700' : 
                  week.status === 'locked' ? 'text-gray-400' : 'text-gray-700'
                }`}>
                  Week {week.week}
                </span>
                {week.status === 'locked' ? (
                  <Lock className="w-3 h-3 md:w-4 md:h-4 mx-auto mt-1 text-gray-300" />
                ) : week.focusTopic ? (
                  <div className="mt-1">
                    <Badge className={`text-[10px] md:text-xs ${SUBJECT_CONFIG[week.focusTopic.subject]?.bgColor || 'bg-gray-100'} ${SUBJECT_CONFIG[week.focusTopic.subject]?.textColor || 'text-gray-600'}`}>
                      {week.focusTopic.subject?.replace('_', ' ').substring(0, 8)}{week.focusTopic.subject?.length > 8 ? '...' : ''}
                    </Badge>
                  </div>
                ) : (
                  <span className="text-[10px] md:text-xs text-gray-400 block mt-1">No data</span>
                )}
                {week.status !== 'locked' && (
                  <div className="mt-1 md:mt-2">
                    <Progress value={getWeekProgress(week)} className="h-1" />
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* All 16 Weeks Overview (Mini) - 8 cols on mobile, 16 on larger */}
        <div className="border-t border-gray-100 pt-3 md:pt-4">
          <p className="text-[10px] md:text-xs text-gray-500 mb-2">All Weeks:</p>
          <div className="grid grid-cols-8 md:grid-cols-16 gap-1">
            {weeks.map((week) => (
              <button
                key={week.week}
                onClick={() => week.status !== 'locked' && setSelectedWeek(week.week)}
                className={`h-5 md:h-6 rounded text-[10px] md:text-xs font-medium transition-all ${
                  selectedWeek === week.week
                    ? 'bg-purple-600 text-white'
                    : week.status === 'locked'
                      ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                      : week.status === 'active'
                        ? 'bg-purple-100 text-purple-600 hover:bg-purple-200 cursor-pointer'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200 cursor-pointer'
                }`}
                disabled={week.status === 'locked'}
                title={`Week ${week.week}${week.focusTopic ? `: ${week.focusTopic.topic_name}` : ''}`}
              >
                {week.week}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Selected Week Details */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader className="pb-2 px-3 md:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <CardTitle className="text-base md:text-lg text-gray-900 flex items-center gap-2">
                <Target className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                Week {selectedWeek} Plan
              </CardTitle>
              {currentWeek?.focusTopic && (
                <CardDescription className="mt-1 text-xs md:text-sm">
                  Focus: <span className="font-medium text-purple-600">
                    {currentWeek.focusTopic.topic_name || currentWeek.focusTopic.topic}
                  </span>
                  {currentWeek.focusTopic.avg_score !== undefined && (
                    <span className="ml-2 text-gray-500">
                      ({Math.round(currentWeek.focusTopic.avg_score)}%)
                    </span>
                  )}
                </CardDescription>
              )}
            </div>
            {currentWeek?.focusTopic && (
              <Badge className={`text-[10px] md:text-xs self-start sm:self-auto ${SUBJECT_CONFIG[currentWeek.focusTopic.subject]?.bgColor || 'bg-gray-100'} ${SUBJECT_CONFIG[currentWeek.focusTopic.subject]?.textColor || 'text-gray-600'}`}>
                {currentWeek.focusTopic.subject?.replace('_', ' ')}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="px-3 md:px-6">
          {currentWeek?.status === 'locked' ? (
            <div className="text-center py-6 md:py-8">
              <Lock className="w-10 h-10 md:w-12 md:h-12 text-gray-300 mx-auto mb-3 md:mb-4" />
              <h3 className="text-base md:text-lg font-medium text-gray-600 mb-2">Week {selectedWeek} Locked</h3>
              <p className="text-gray-500 text-xs md:text-sm mb-4">
                Complete more assessments to unlock personalized content for this week.
              </p>
              <Button onClick={() => navigate('/ks2-assessment')} variant="outline" size="sm">
                Take Adaptive Assessment
              </Button>
            </div>
          ) : currentWeek?.days?.length > 0 ? (
            <>
              {/* Mobile: Horizontal scrollable or stacked layout */}
              <div className="block md:hidden">
                <div className="flex overflow-x-auto pb-2 gap-2 -mx-3 px-3 snap-x snap-mandatory">
                  {currentWeek.days.map((day) => (
                    <div
                      key={day.day}
                      className="flex-shrink-0 w-[140px] border border-gray-200 rounded-lg p-2 hover:border-purple-300 transition-all snap-start"
                    >
                      <div className="text-center mb-2">
                        <span className="text-[10px] font-medium text-gray-500">{day.label}</span>
                        <div className="text-xs font-bold text-gray-700">Day {day.day}</div>
                      </div>
                      
                      <div className="space-y-1.5">
                        {day.tasks.map((task, idx) => {
                          const taskConfig = TASK_TYPE_CONFIG[task.type];
                          const TaskIcon = taskConfig?.icon || Target;
                          
                          return (
                            <button
                              key={idx}
                              onClick={() => handleTaskClick(task, day.label)}
                              className={`w-full p-1.5 rounded-lg border transition-all ${
                                completedTasks[task.id]
                                  ? 'bg-green-50 border-green-200'
                                  : `${taskConfig?.bgColor || 'bg-gray-100'} border-gray-200 hover:border-purple-300`
                              }`}
                              data-testid={`task-${task.id}`}
                            >
                              <div className="flex items-center gap-1.5">
                                {completedTasks[task.id] ? (
                                  <CheckCircle2 className="w-3 h-3 text-green-500" />
                                ) : (
                                  <TaskIcon className={`w-3 h-3 ${taskConfig?.textColor || 'text-gray-600'}`} />
                                )}
                                <span className={`text-[10px] font-medium ${completedTasks[task.id] ? 'text-green-600' : 'text-gray-700'}`}>
                                  {taskConfig?.label}
                                </span>
                              </div>
                              <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1">
                                <Clock className="w-2.5 h-2.5" />
                                {task.estimatedTime}m
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 text-center mt-2">← Swipe to see all days →</p>
              </div>
              
              {/* Desktop: 5-column grid */}
              <div className="hidden md:grid grid-cols-5 gap-3">
                {currentWeek.days.map((day) => (
                  <div
                    key={day.day}
                    className="border border-gray-200 rounded-lg p-3 hover:border-purple-300 transition-all"
                  >
                    <div className="text-center mb-2">
                      <span className="text-xs font-medium text-gray-500">{day.label}</span>
                      <div className="text-sm font-bold text-gray-700">Day {day.day}</div>
                    </div>
                    
                    <div className="space-y-2">
                      {day.tasks.map((task, idx) => {
                        const taskConfig = TASK_TYPE_CONFIG[task.type];
                        const TaskIcon = taskConfig?.icon || Target;
                        
                        return (
                          <button
                            key={idx}
                            onClick={() => handleTaskClick(task, day.label)}
                            className={`w-full p-2 rounded-lg border transition-all ${
                              completedTasks[task.id]
                                ? 'bg-green-50 border-green-200'
                                : `${taskConfig?.bgColor || 'bg-gray-100'} border-gray-200 hover:border-purple-300`
                            }`}
                            data-testid={`task-${task.id}`}
                          >
                            <div className="flex items-center gap-2">
                              {completedTasks[task.id] ? (
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                              ) : (
                                <TaskIcon className={`w-4 h-4 ${taskConfig?.textColor || 'text-gray-600'}`} />
                              )}
                              <span className={`text-xs font-medium ${completedTasks[task.id] ? 'text-green-600' : 'text-gray-700'}`}>
                                {taskConfig?.label}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {task.estimatedTime}m
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-6 md:py-8">
              <Calendar className="w-10 h-10 md:w-12 md:h-12 text-gray-300 mx-auto mb-3 md:mb-4" />
              <h3 className="text-base md:text-lg font-medium text-gray-600 mb-2">No Content Yet</h3>
              <p className="text-gray-500 text-xs md:text-sm mb-4">
                Take an assessment to generate your personalized learning plan.
              </p>
              <Button onClick={() => navigate('/ks2-assessment')} size="sm">
                <Play className="w-4 h-4 mr-2" />
                Start Assessment
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Week 5-16 Notice */}
      {selectedWeek > 4 && currentWeek?.status !== 'locked' && !currentWeek?.days?.length && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-start gap-2 md:gap-3">
              <Lightbulb className="w-4 h-4 md:w-5 md:h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-800 text-sm md:text-base">Dynamic Content</h4>
                <p className="text-xs md:text-sm text-amber-700 mt-1">
                  Content for weeks 5-16 will be generated automatically based on your latest assessment scores. 
                  Keep practicing to see updated recommendations!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Task Modal */}
      <Dialog open={taskModalOpen} onOpenChange={setTaskModalOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base md:text-lg">
              {selectedTask && TASK_TYPE_CONFIG[selectedTask.type] && (
                <>
                  {(() => {
                    const TaskIcon = TASK_TYPE_CONFIG[selectedTask.type].icon;
                    return <TaskIcon className={`w-4 h-4 md:w-5 md:h-5 ${TASK_TYPE_CONFIG[selectedTask.type].textColor}`} />;
                  })()}
                </>
              )}
              <span className="line-clamp-2">{selectedTask?.title}</span>
            </DialogTitle>
            <DialogDescription className="text-xs md:text-sm">
              {selectedTask?.dayLabel} • {selectedTask?.estimatedTime} minutes
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 md:space-y-4">
            <p className="text-gray-600 text-sm">{selectedTask?.description}</p>

            {/* Gamification strip — XP reward + difficulty + estimated time
                pills give learners a clear "what do I get" before tapping. */}
            <div
              className="flex items-center justify-between gap-2 rounded-lg bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 border border-amber-200 px-3 py-2"
              data-testid="task-xp-strip"
            >
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                <span className="text-xs md:text-sm font-semibold text-amber-700">
                  Earn +{({ revision: 10, practice: 15, quiz: 25 })[selectedTask?.type] || 15} XP
                </span>
              </div>
              <div className="flex items-center gap-1 text-[10px] md:text-xs text-amber-700/80">
                <Clock className="w-3 h-3" />
                <span>{selectedTask?.estimatedTime || 15} min</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge className={`text-[10px] md:text-xs ${SUBJECT_CONFIG[selectedTask?.subject]?.bgColor || 'bg-gray-100'} ${SUBJECT_CONFIG[selectedTask?.subject]?.textColor || 'text-gray-600'}`}>
                {selectedTask?.subject?.replace('_', ' ')}
              </Badge>
              <Badge variant="outline" className="capitalize text-[10px] md:text-xs">
                {selectedTask?.difficulty}
              </Badge>
            </div>
            
            {selectedTask?.type === 'revision' && (
              <div className="bg-blue-50 p-2.5 md:p-3 rounded-lg">
                <h4 className="font-medium text-blue-800 text-xs md:text-sm mb-1">Tips for Revision:</h4>
                <ul className="text-[10px] md:text-xs text-blue-700 space-y-0.5 md:space-y-1">
                  <li>• Read through the concept explanation carefully</li>
                  <li>• Take notes on key points</li>
                  <li>• Try to explain the concept in your own words</li>
                </ul>
              </div>
            )}
            
            {selectedTask?.type === 'practice' && (
              <div className="bg-purple-50 p-2.5 md:p-3 rounded-lg">
                <h4 className="font-medium text-purple-800 text-xs md:text-sm mb-1">Tips for Practice:</h4>
                <ul className="text-[10px] md:text-xs text-purple-700 space-y-0.5 md:space-y-1">
                  <li>• Read each question carefully before answering</li>
                  <li>• Show your working where possible</li>
                  <li>• Review any incorrect answers</li>
                </ul>
              </div>
            )}
            
            {selectedTask?.type === 'quiz' && (
              <div className="bg-amber-50 p-2.5 md:p-3 rounded-lg">
                <h4 className="font-medium text-amber-800 text-xs md:text-sm mb-1">Quiz Tips:</h4>
                <ul className="text-[10px] md:text-xs text-amber-700 space-y-0.5 md:space-y-1">
                  <li>• Time yourself for exam practice</li>
                  <li>• Don't spend too long on one question</li>
                  <li>• Check your answers if time permits</li>
                </ul>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button 
                onClick={handleStartTask}
                className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-sm shadow-md hover:shadow-lg transition-shadow"
                size="sm"
                data-testid="task-start-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {selectedTask ? (resolveTaskTarget(selectedTask, currentUser).label || 'Start Task') : 'Start Task'}
              </Button>
              <Button
                variant={completedTasks[selectedTask?.id] ? "outline" : "default"}
                onClick={() => {
                  if (selectedTask?.id) toggleTaskComplete(selectedTask.id);
                }}
                size="sm"
                className={completedTasks[selectedTask?.id]
                  ? "border-green-300 text-green-700 hover:bg-green-50"
                  : "bg-green-600 hover:bg-green-700 text-white"
                }
                data-testid="toggle-task-complete-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                {completedTasks[selectedTask?.id] ? 'Undo' : 'Done'}
              </Button>
              <Button 
                variant="outline"
                onClick={() => setTaskModalOpen(false)}
                size="sm"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SixteenWeekCalendar;
